export interface BusinessSignupParams {
  username?: string;
  companyEmail?: string;
  phone?: string;
  regionCode?: string;
  password?: string;
  verficationCode?: string;
  emailVerified?: string;
  phoneVerified?: string;
  profile?: string;
  companyName?: string;
  companyAddress?: string;
  brNumber?: string;
}

type BusinessSignupImageSlot = "profile" | "brImage";

const PROFILE_KEY = "uninfo_business_signup_profile";
const BR_IMAGE_KEY = "uninfo_business_signup_br_image";

const IMAGE_KEYS: Record<BusinessSignupImageSlot, string> = {
  profile: PROFILE_KEY,
  brImage: BR_IMAGE_KEY,
};

const memoryImages: Partial<Record<BusinessSignupImageSlot, string>> = {};

const isQuotaExceeded = (err: unknown): boolean => {
  const { name, code } = (err || {}) as { name?: string; code?: number };
  return (
    name === "QuotaExceededError" ||
    name === "NS_ERROR_DOM_QUOTA_REACHED" ||
    code === 22 ||
    code === 1014
  );
};

const setBusinessSignupImage = (
  slot: BusinessSignupImageSlot,
  dataUrl: string,
): boolean => {
  memoryImages[slot] = dataUrl;
  const key = IMAGE_KEYS[slot];
  try {
    sessionStorage.setItem(key, dataUrl);
    return true;
  } catch (err) {
    try {
      sessionStorage.removeItem(key);
    } catch {
      void 0;
    }
    if (!dataUrl) return true;
    if (isQuotaExceeded(err)) {
      console.error(
        `[businessSignupState] sessionStorage quota exceeded while saving ${slot} (${dataUrl.length} chars), keeping it in memory for this tab: ${err}`,
      );
    } else {
      console.error(
        `[businessSignupState] could not save ${slot} to sessionStorage, keeping it in memory for this tab: ${err}`,
      );
    }
    return false;
  }
};

const getBusinessSignupImage = (slot: BusinessSignupImageSlot): string => {
  const inMemory = memoryImages[slot];
  if (inMemory !== undefined) return inMemory;
  try {
    return sessionStorage.getItem(IMAGE_KEYS[slot]) || "";
  } catch {
    return "";
  }
};

export const buildBusinessSignupQuery = (params: BusinessSignupParams): string => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) search.set(key, value);
  });
  const qs = search.toString();
  return qs ? `?${qs}` : "";
};

export const readBusinessSignupParams = (search: string): BusinessSignupParams => {
  const params = new URLSearchParams(search);
  const out: BusinessSignupParams = {};
  [
    "username",
    "companyEmail",
    "phone",
    "regionCode",
    "password",
    "verficationCode",
    "emailVerified",
    "phoneVerified",
    "profile",
    "companyName",
    "companyAddress",
    "brNumber",
  ].forEach((key) => {
    const value = params.get(key);
    if (value) out[key as keyof BusinessSignupParams] = value;
  });
  return out;
};

export const setBusinessSignupProfile = (dataUrl: string) =>
  setBusinessSignupImage("profile", dataUrl);

export const getBusinessSignupProfile = (): string =>
  getBusinessSignupImage("profile");

export const setBusinessSignupBrImage = (dataUrl: string) =>
  setBusinessSignupImage("brImage", dataUrl);

export const getBusinessSignupBrImage = (): string =>
  getBusinessSignupImage("brImage");

export const clearBusinessSignupImages = () => {
  (Object.keys(IMAGE_KEYS) as BusinessSignupImageSlot[]).forEach((slot) => {
    delete memoryImages[slot];
    try {
      sessionStorage.removeItem(IMAGE_KEYS[slot]);
    } catch {
      void 0;
    }
  });
};
const PENDING_KEY = "uninfo_business_signup_pending";

export interface BusinessSignupPendingPayload {
  username?: string;
  companyEmail?: string;
  phone?: string;
  regionCode?: string;
  password?: string;
  companyName?: string;
  companyAddress?: string;
  brNumber?: string;
  plan_type?: "monthly" | "yearly";
  terms_version?: string;
  terms_accepted?: boolean;
  br_image?: string;
  profile_icon?: string;
}

export const saveBusinessSignupPending = (payload: BusinessSignupPendingPayload): void => {
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(payload));
  } catch (err) {
    console.error("[businessSignupState] could not save pending payload", err);
  }
};

export const getBusinessSignupPending = (): BusinessSignupPendingPayload | null => {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as BusinessSignupPendingPayload;
  } catch (err) {
    console.error("[businessSignupState] could not read pending payload", err);
    return null;
  }
};

export const clearBusinessSignupPending = (): void => {
  try {
    sessionStorage.removeItem(PENDING_KEY);
  } catch (err) {
    console.error("[businessSignupState] could not clear pending payload", err);
  }
};

const REGISTERED_KEY = "uninfo_business_signup_registered";

export const markBusinessSignupRegistered = (): void => {
  try {
    sessionStorage.setItem(REGISTERED_KEY, "1");
  } catch (err) {
    console.error("[businessSignupState] could not mark registered", err);
  }
};

export const isBusinessSignupRegistered = (): boolean => {
  try {
    return sessionStorage.getItem(REGISTERED_KEY) === "1";
  } catch {
    return false;
  }
};

export const clearBusinessSignupRegistered = (): void => {
  try {
    sessionStorage.removeItem(REGISTERED_KEY);
  } catch (err) {
    console.error("[businessSignupState] could not clear registered flag", err);
  }
};

